import {
  Autocomplete,
  Button,
  Divider,
  Flex,
  Grid,
  Label,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import SquareIcon from "@mui/icons-material/Square";
import { HexColorPicker } from "react-colorful";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import useErrors from "hooks/useErrors";
import DevicesChoose from "components/devices/DevicesChoose";
import LabelRequired from "components/utils/LabelRequired";
import { globalStrings } from "config/translations/es";
import PhoneList from "components/phone/PhoneList";
import InputList from "components/utils/InputList";
import ConditionForm from "./ConditionForm";
import {
  deviceVariablesByDeviceID,
  listCompanies,
  listVariables,
} from "graphql/queries";
import { LayoutContext } from "layout/context/LayoutContext";
import AlertSchedule from "./AlertSchedule";
import CustomPopover from "components/utils/CustomPopover";

const AlertForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const [variables, setVariables] = useState([]);
  const [isLoadingVariables, setIsLoadingVariables] = useState(false);
  const [alertForm, setAlertForm] = useState(initialValues);
  const [resetValues, setResetValues] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const validateErrors = useErrors({
    name: "isRequired",
    severity: "isRequired",
    companyID: "isRequired",
  });
  const validateListErrors = useErrors({
    firstParam: "isRequired",
    operator: "isRequired",
    secondParam: "isRequired",
    time: "isRequired",
    timeValue: "isRequired",
    grouper: "isRequired",
    phone: "isPhone",
    email: "isEmail",
  });

  const onSubmitForm = () => {
    let hasErrors = false;

    Object.entries(alertForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      !companies.some((companyRec) => companyRec.id === alertForm.companyID)
    ) {
      setErrors((prevSt) => ({
        ...prevSt,
        companyID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (
      alertForm.wspNotifications?.some(
        (wsp) => !!validateListErrors("phone", wsp)
      ) ||
      alertForm.smsNotifications?.some(
        (sms) => !!validateListErrors("phone", sms)
      )
    ) {
      toast.error(strings.wspNotifications.invalid);
      return;
    }

    if (
      alertForm.emailNotifications?.some(
        (email) => !!validateListErrors("email", email)
      )
    ) {
      toast.error(strings.emailNotifications.invalid);
      return;
    }

    if (
      alertForm.conditions?.some((cond) =>
        Object.entries(cond).some(
          ([name, value]) => !!validateListErrors(name, value)
        )
      ) ||
      !alertForm.conditions?.every((cond) =>
        variables.some((variableIt) => variableIt.id === cond.firstParam)
      )
    ) {
      toast.error(strings.conditions.fieldsMissing);
      return;
    }

    if (!hasErrors)
      onSubmit({
        ...alertForm,
        smsNotifications: alertForm.smsNotifications?.filter((sms) => !!sms),
        wspNotifications: alertForm.wspNotifications?.filter((wsp) => !!wsp),
        emailNotifications: alertForm.emailNotifications?.filter(
          (email) => !!email
        ),
        companyID: alertForm.companyID || null,
      });
  };

  const onClearSelect = () => {
    const value = "";
    fetchCompanies({ name: value });
    setAlertForm((prevSt) => ({
      ...prevSt,
      companyID: value,
    }));
  };

  const onSelect = (name, value) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    fetchCompanies({ name: value });
    setAlertForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChange = (e) => {
    let { name, value } = e.target;

    setAlertForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onChangeSmsNotifications = (list) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      smsNotifications: list ?? [],
    }));
  };

  const onChangeWspNotifications = (list) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      wspNotifications: list ?? [],
    }));
  };

  const onChangeEmailNotifications = (list) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      emailNotifications: list ?? [],
    }));
  };

  const onChangeConditions = (cond) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      conditions: cond ?? [],
    }));
  };

  const onChangeDevices = (devices) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      devices,
    }));
  };

  const onChangeColor = (color) => {
    setAlertForm((prevSt) => ({
      ...prevSt,
      severityColor: color,
    }));
  };

  const onChangeSchedule = (name, value) => {
    if (name === "execInterval") {
      if (value === "hour")
        setAlertForm((prevSt) => ({
          ...prevSt,
          execTime: "",
          hourInterval: 1,
        }));
      else
        setAlertForm((prevSt) => ({
          ...prevSt,
          startSchedule: "",
          endSchedule: "",
          hourInterval: null,
        }));
    }
    setAlertForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({});
    setAlertForm(initialValues);
    setResetValues((prev) => !prev);
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  async function fetchDeviceVariables(devices) {
    try {
      setIsLoadingVariables(true);
      const variablesData = await API.graphql(graphqlOperation(listVariables));
      const variablesItems = variablesData.data.listVariables.items;

      if (devices?.length > 0) {
        const deviceVariablesData = await Promise.all(
          devices?.map((device) =>
            API.graphql(
              graphqlOperation(deviceVariablesByDeviceID, {
                deviceID: device.id,
              })
            )
          )
        );

        const variablesByDevice = deviceVariablesData?.map(
          (devVarData) =>
            devVarData?.data?.deviceVariablesByDeviceID?.items?.map(
              (devVarItem) => devVarItem.variableID
            ) ?? []
        );

        let uniqueVariables = [];

        variablesByDevice?.forEach((listVariables) => {
          const listGetted = listVariables.filter((variable) =>
            variablesByDevice.every((listVariablesSec) =>
              listVariablesSec.some((variableSec) => variableSec === variable)
            )
          );
          uniqueVariables.push(...listGetted);
        });

        uniqueVariables = [...new Set(uniqueVariables)];

        if (uniqueVariables?.length > 0) {
          setVariables(
            variablesItems?.filter((variable) =>
              uniqueVariables.some((unique) => variable.id === unique)
            )
          );
        } else setVariables([]);
      } else {
        setVariables(variablesItems);
      }
      setIsLoadingVariables(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchVariables);
    }
  }

  useEffect(() => {
    fetchDeviceVariables(alertForm.devices);
  }, [alertForm.devices]);

  useEffect(() => {
    setAlertForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Grid rowGap="15px" columnGap="15px" padding="20px">
      <Grid rowGap="15px" columnGap="15px" templateColumns={{ medium: "1fr" }}>
        <TextField
          name="name"
          label={<LabelRequired text={strings.fields.name.label} />}
          value={alertForm.name}
          onChange={onChange}
          errorMessage={errors?.name}
          hasError={!!errors?.name}
        ></TextField>
        <TextAreaField
          name="description"
          label={strings.fields.description.label}
          value={alertForm.description}
          onChange={onChange}
        ></TextAreaField>
      </Grid>
      <Grid
        rowGap="15px"
        columnGap="15px"
        templateColumns={{ medium: "1fr 1fr" }}
      >
        <SelectField
          name="severity"
          value={alertForm.severity}
          label={<LabelRequired text={strings.fields.severity.label} />}
          onChange={onChange}
          errorMessage={errors?.severity}
          hasError={!!errors?.severity}
        >
          <option value="">{globalStrings.selectOption}</option>
          <option value="1">{globalStrings.severity.options["1"]}</option>
          <option value="2">{globalStrings.severity.options["2"]}</option>
          <option value="3">{globalStrings.severity.options["3"]}</option>
        </SelectField>
        <TextField
          name="severityColor"
          label={strings.fields.severityColor.label}
          value={alertForm.severityColor}
          onChange={onChange}
          outerEndComponent={
            <CustomPopover
              content={
                <HexColorPicker
                  style={{ margin: "20px" }}
                  color={alertForm.severityColor}
                  onChange={onChangeColor}
                />
              }
            >
              <Button color={alertForm.severityColor}>
                <SquareIcon />
              </Button>
            </CustomPopover>
          }
        ></TextField>
      </Grid>
      {type === "create" && (
        <>
          <Label htmlFor="companyID">
            {<LabelRequired text={strings.fields.company.label} />}
          </Label>
          <div style={{ marginTop: "-5px" }}>
            <Autocomplete
              id="companyID"
              name="companyID"
              label={<LabelRequired text={strings.fields.company.label} />}
              isRequired
              placeholder={strings.fields.company.placeholder}
              value={
                companies.find(
                  (companyRec) => companyRec.id === alertForm.companyID
                )?.name ??
                alertForm.companyID ??
                ""
              }
              options={companies.map((companyRec) => ({
                id: companyRec.id,
                label: companyRec.name,
              }))}
              isLoading={companiesLoading}
              onClear={() => onClearSelect("companyID")}
              onChange={onChangeSelect}
              onSelect={({ id }) => onSelect("companyID", id)}
              errorMessage={errors?.companyID}
              hasError={!!errors?.companyID}
              menuSlots={{ Empty: globalStrings.noResults }}
            ></Autocomplete>
          </div>
        </>
      )}
      <Divider className="mt-3 mb-2" />
      <AlertSchedule form={alertForm} onChange={onChangeSchedule} />
      <Divider className="mt-3 mb-2" />
      <DevicesChoose
        initDevicesGranted={initialValues.devices}
        onChangeDevicesGranted={onChangeDevices}
        resetValues={resetValues}
        company={alertForm.companyID}
      />
      <Divider className="mt-3 mb-2" />
      <PhoneList
        title={strings.smsNotifications.title}
        values={alertForm.smsNotifications ?? []}
        onChange={onChangeSmsNotifications}
      />
      <Divider className="mt-1 mb-2" />
      <PhoneList
        title={strings.wspNotifications.title}
        values={alertForm.wspNotifications ?? []}
        onChange={onChangeWspNotifications}
      />
      <Divider className="mt-1 mb-2" />
      <InputList
        title={strings.emailNotifications.title}
        values={alertForm.emailNotifications ?? []}
        onChange={onChangeEmailNotifications}
        inputPlaceholder={strings.emailNotifications.inputPlaceholder}
        inputType="email"
      />
      <Divider className="mt-1 mb-2" />
      <ConditionForm
        title={strings.conditions.title}
        values={alertForm.conditions ?? []}
        onChange={onChangeConditions}
        variables={variables ?? []}
        isLoadingVariables={isLoadingVariables}
      />
      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={resetFormValues}
        ></Button>
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
          isDisabled={Object.values(errors).some((e) => !!e)}
        ></Button>
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    responses: {
      errorFetchVariables: "Error al consultar variables",
      errorFetchCompanies: "Error al consultar empresas",
    },
    fields: {
      name: {
        label: "Nombre",
      },
      description: {
        label: "Descripción",
      },
      severity: {
        label: "Severidad",
      },
      severityColor: {
        label: "Color de Severidad",
      },
      startSchedule: {
        label: "Hora de Inicio",
      },
      endSchedule: {
        label: "Hora de Fin",
      },
      days: {
        label: "Día de la Semana",
      },
      executeOnHolidays: {
        label: "Ejecutar alerta en días festivos",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    selectOption: "Seleccione una opción",
    smsNotifications: {
      title: "Notificaciones por SMS",
    },
    wspNotifications: {
      title: "Notificaciones por Whatsapp",
      invalid: "Teléfonos inválidos",
    },
    emailNotifications: {
      title: "Notificaciones por Correo",
      invalid: "Correos inválidos",
      inputPlaceholder: "Ingrese correo",
    },
    conditions: {
      title: "Condiciones",
      fieldsMissing: "Faltan campos por rellenar en la sección de Condiciones",
      isRequired: "Debe existir al menos 1 condición",
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
  },
});

export default AlertForm;
